import { z } from 'zod'

import { paginacaoSearchFields, toLimitOffset } from '@/widgets/data-table/table-search'

import {
  OPEN_STATUSES,
  SETTLED_STATUSES,
  VISIBLE_STATUSES,
  type BillDirection,
  type BillListParams,
  type BillStatus,
} from './api'

/**
 * Search params de Pagamentos › Pagar e Recebimentos › Receber.
 *
 * `GET /v1/bills` (composto: agendamento contábil + transação do Hatch) aceita:
 * PERÍODO de vencimento (`de`/`ate`), STATUS (`situacao`), DATA DA BAIXA
 * (`pagoDe`/`pagoAte`), FAIXA DE VALOR (`valorMin`/`valorMax`) e CATEGORIA do
 * rateio (`categoria`). Os de valor/baixa são uniformes nas duas origens; a
 * categoria só existe no contábil — filtrar por ela EXCLUI a origem bancária (o
 * banco não tem categoria nossa, o backend a deixa de fora). Contato e busca
 * textual vêm nas próximas fases. Filtro que o endpoint não suporta NÃO entra
 * aqui: viraria filtragem só da página atual, silenciosamente errada.
 *
 * `direction` é identidade da tela, não param editável.
 */
export const billsSearchSchema = z.object({
  ...paginacaoSearchFields,
  de: z.iso.date().optional(),
  ate: z.iso.date().optional(),
  /** Data da BAIXA (recebimento/pagamento). */
  pagoDe: z.iso.date().optional(),
  pagoAte: z.iso.date().optional(),
  /** Faixa de valor (Money como string decimal). */
  valorMin: z.string().optional(),
  valorMax: z.string().optional(),
  /** Categoria do rateio (só origem contábil; banco sai da lista quando setada). */
  categoria: z.string().optional(),
  /** Busca textual (Nome da contraparte OU Descrição) — vale nas duas origens. */
  q: z.string().optional(),
  /**
   * VISÃO, não status cru: cada uma vira um conjunto de status do backend.
   * "Canceladas" saiu — cancelado não existe mais na interface.
   */
  situacao: z.enum(['abertas', 'baixadas']).optional(),
  ordenar: z
    .enum([
      'due_date',
      '-due_date',
      'amount',
      '-amount',
      'status',
      '-status',
      'created_at',
      '-created_at',
      'settled_on',
      '-settled_on',
    ])
    .default('due_date'),
})

export type BillsSearch = z.infer<typeof billsSearchSchema>
export type BillsSituacao = NonNullable<BillsSearch['situacao']>

/** Ordem default do endpoint — usada quando o 3º clique destrava o header. */
export const BILLS_ORDEM_DEFAULT = 'due_date' satisfies BillsSearch['ordenar']

/** Chaves de filtro da tela — o que "limpar filtros" zera e o que vira chip. */
export const BILLS_FILTROS = [
  'de',
  'ate',
  'pagoDe',
  'pagoAte',
  'valorMin',
  'valorMax',
  'situacao',
  'categoria',
  'q',
] as const satisfies readonly (keyof BillsSearch)[]

/**
 * Visão da tela → conjunto de status do backend.
 *
 * Um mapa e não um status solto: "em aberto" na origem bancária é
 * `open|overdue|partial`, e mandar só `open` esconderia justamente as vencidas.
 */
export const SITUACAO_STATUS: Record<BillsSituacao, readonly BillStatus[]> = {
  abertas: OPEN_STATUSES,
  baixadas: SETTLED_STATUSES,
}

/**
 * Search params → params da API. ESTA FUNÇÃO É A FRONTEIRA: o que não sai daqui
 * não vira request e, portanto, não pode filtrar só a página atual.
 *
 * Mesmo SEM situação escolhida, `status` vai EXPLÍCITO (VISIBLE_STATUSES) — é
 * assim que `canceled` é excluído no nível da query: a lista pede o resto em vez
 * de confiar que o backend não devolva cancelado.
 */
export function toBillListParams(s: BillsSearch, direction: BillDirection): BillListParams {
  return {
    direction,
    status: s.situacao !== undefined ? SITUACAO_STATUS[s.situacao] : VISIBLE_STATUSES,
    ...(s.de !== undefined && { due_after: s.de }),
    ...(s.ate !== undefined && { due_before: s.ate }),
    ...(s.pagoDe !== undefined && { settled_after: s.pagoDe }),
    ...(s.pagoAte !== undefined && { settled_before: s.pagoAte }),
    ...(s.valorMin !== undefined && { amount_min: s.valorMin }),
    ...(s.valorMax !== undefined && { amount_max: s.valorMax }),
    ...(s.categoria !== undefined && { category_id: s.categoria }),
    ...(s.q !== undefined && { q: s.q }),
    order_by: s.ordenar,
    ...toLimitOffset(s),
  }
}
